import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface AccessibilityState {
  fontSize: "normal" | "large" | "larger";
  highContrast: boolean;
  highlightLinks: boolean;
  reduceMotion: boolean;
}

interface AccessibilityContextType extends AccessibilityState {
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  toggleHighContrast: () => void;
  toggleHighlightLinks: () => void;
  toggleReduceMotion: () => void;
  resetAll: () => void;
}

const defaultState: AccessibilityState = {
  fontSize: "normal",
  highContrast: false,
  highlightLinks: false,
  reduceMotion: false,
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error("useAccessibility must be used within an AccessibilityProvider");
  }
  return context;
};

interface AccessibilityProviderProps {
  children: ReactNode;
}

export const AccessibilityProvider = ({ children }: AccessibilityProviderProps) => {
  const [state, setState] = useState<AccessibilityState>(() => {
    // Load from localStorage if available
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("accessibility-settings");
      if (saved) {
        try {
          return { ...defaultState, ...JSON.parse(saved) };
        } catch {
          return defaultState;
        }
      }
    }
    return defaultState;
  });

  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem("accessibility-settings", JSON.stringify(state));
  }, [state]);

  // Apply font size to document
  useEffect(() => {
    const root = document.documentElement;
    switch (state.fontSize) {
      case "large":
        root.style.fontSize = "112.5%"; // 18px
        break;
      case "larger":
        root.style.fontSize = "125%"; // 20px
        break;
      default:
        root.style.fontSize = "100%"; // 16px
    }
  }, [state.fontSize]);

  // Apply high contrast mode
  useEffect(() => {
    document.body.classList.toggle("high-contrast", state.highContrast);
  }, [state.highContrast]);

  // Apply highlight links
  useEffect(() => {
    document.body.classList.toggle("highlight-links", state.highlightLinks);
  }, [state.highlightLinks]);

  // Apply reduce motion
  useEffect(() => {
    document.body.classList.toggle("reduce-motion", state.reduceMotion);
  }, [state.reduceMotion]);

  const increaseFontSize = () => {
    setState((prev) => ({
      ...prev,
      fontSize:
        prev.fontSize === "normal"
          ? "large"
          : prev.fontSize === "large"
          ? "larger"
          : "larger",
    }));
  };

  const decreaseFontSize = () => {
    setState((prev) => ({
      ...prev,
      fontSize:
        prev.fontSize === "larger"
          ? "large"
          : prev.fontSize === "large"
          ? "normal"
          : "normal",
    }));
  };

  const toggleHighContrast = () => {
    setState((prev) => ({ ...prev, highContrast: !prev.highContrast }));
  };

  const toggleHighlightLinks = () => {
    setState((prev) => ({ ...prev, highlightLinks: !prev.highlightLinks }));
  };

  const toggleReduceMotion = () => {
    setState((prev) => ({ ...prev, reduceMotion: !prev.reduceMotion }));
  };

  const resetAll = () => {
    setState(defaultState);
  };

  return (
    <AccessibilityContext.Provider
      value={{
        ...state,
        increaseFontSize,
        decreaseFontSize,
        toggleHighContrast,
        toggleHighlightLinks,
        toggleReduceMotion,
        resetAll,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};
